import * as angular from 'angular';
import * as _ from 'lodash';
import { ISorterColumnService } from 'src/app/shared/sorter-column/sorter-column.service';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { SorterColumnDataType } from '../../../../ng2/shared/constants/sorter-column-data-type.constant';
import { SorterColumnNumberType } from '../../../../ng2/shared/constants/sorter-column-number-type.constant';
import { StudentFetchService } from '../../../../ng2/shared/services/student-fetch/student-fetch.service';
import { ISchool } from '../../../../ng2/shared/typings/interfaces/school.interface';
import { IStudent } from '../../../../ng2/shared/typings/interfaces/student.interface';
import { IUserMini } from '../../../../ng2/shared/typings/interfaces/user.interface';
import { TValidSdcColumns } from './../../../sdc/sdc-columns.type';
import { IColumnSort, TValidBundleNames, TValidJoins } from './../../../sdc/sdc-state/sdc-state.typings';
import { ISorterColumn } from './../../sorter-column/sorter-column.service';
import { CellRenderer } from './ag-grid/cell-renderer.service';

export interface IFetchStudentParams {
  schoolId: string;
  columnKeys?: TValidSdcColumns[];
  projection?: string[];
  joins?: TValidJoins[];
  loadedProjection?: { [key: string]: any };
  loadedJoins?: string[];
  bundleName?: TValidBundleNames;
  forceDataRefetch?: boolean;
  whereQuery?: boolean;
}

export class StudentSetService {
  static $inject = [
    'SorterColumn',
    'AgGridFilterService',
    'Student',
    '$q',
    'UtilitiesService',
    'NumberFilter',
    'BooleanYesNoFilter',
    '$http',
    'PublicConfig',
    'Rollbar',
    'StudentFetchService',
    'ImUser',
  ];

  constructor(
    private SorterColumn: ISorterColumnService,
    private AgGridFilterService,
    private Student,
    private $q,
    private UtilitiesService,
    private NumberFilter,
    private BooleanYesNoFilter,
    private $http,
    private PublicConfig,
    private Rollbar,
    private studentFetchService: StudentFetchService,
    private imUser: ImUser,
  ) {}

  /**
   * Fetch students from the API
   *
   * @param {boolean} rawPojo true if you want POJOs returned, not Student objects
   * @param {object} loadedProjection the projection that is already loaded (will be excluded from the fetch)
   * @param {array} loadedJoins the joins that are already loaded (will be excluded from fetch)
   * @returns {Promise} resolved with an object like `{ students, projection }` where students is an array of
   *   partial students just fetched, and projection is the projection that was fetched
   *   @note This promise is rejected with err.status of -1 if the fetch request
   *   is cancelled due to another `fetch`
   *   or `fetchColumns` call.
   */
  fetchStudents({
    schoolId,
    columnKeys,
    joins,
    loadedProjection,
    loadedJoins,
    bundleName,
    forceDataRefetch = false,
    whereQuery = true,
  }: IFetchStudentParams) {
    let deferred = this.$q.defer();

    // Calculate the difference between the loadedProjection (if it exists) and the
    // sum of requested column/ path projections
    let diffProjection;
    const columnProjection = this.getProjectionForColumnKeys(columnKeys);

    if (_.size(loadedProjection)) {
      diffProjection = this.UtilitiesService.diffProjection(loadedProjection, columnProjection);
    } else {
      diffProjection = columnProjection;
    }

    // Calculate the difference between the loadedJoins (if it exists) and the
    // sum of requested column/ parameter joins
    let diffJoins;
    const columnJoins = this._getJoinsForColumnKeys(columnKeys);
    const fullJoins = _.union(columnJoins, joins);

    if (loadedJoins && loadedJoins.length > 0) {
      diffJoins = _.difference(fullJoins, loadedJoins);
    } else {
      diffJoins = fullJoins;
    }

    // Always include at least the _id in the projection
    diffProjection._id = true;

    const skipProjectionReload = angular.equals({ _id: true }, diffProjection);
    const skipJoinReload = diffJoins.length === 0;

    if (skipProjectionReload && skipJoinReload && !forceDataRefetch) {
      // Don't need any new data, so just return
      deferred.resolve({ noNewData: true });
    } else {
      // fetch student data
      this.studentFetchService
        .fetchStudentData({
          schoolId,
          bundleName,
          projection: diffProjection,
          fullProjection: columnProjection, // columnProjection projection is needed to log bundle mismatched (CM)
          joins: diffJoins,
          whereQuery,
        })
        .then(data => {
          return deferred.resolve({
            students: data,
            projection: diffProjection,
            joins: diffJoins,
          });
        })
        .catch(deferred.reject);
    }

    return deferred.promise;
  }

  /**
   * Returns the set of flattened students
   *
   * @returns {Array} An array of flattened students like this:
   *    [
   *       {
   *          _id: "1234513W320
   *          studentId: '12345',
   *          studentName: 'John Smith'
   *       }
   *       // .. other students ..
   *    ]
   *
   *    The keys of these students are sorter column keys, and the values are the projected MongoDB values.
   *
   */
  flattenStudents(students: IStudent[], school: ISchool, columnKeys: TValidSdcColumns[]) {
    const flattenedStudents = _.map(students, student => this._flattenStudent(student, school, columnKeys));
    return flattenedStudents;
  }

  filterStudents(students, flattenedStudents, filter) {
    // Array < Student >, Array <FlattenedStudent>, {}
    // returns an object w/ `flattenedStudents` & `students` keys

    const filteredStudents = {
      flattenedStudents: [],
      students: [],
    };

    const filterFunctions = this.AgGridFilterService.makeFilterFunctionsFromFilterSet(filter);

    if (!filter) {
      filteredStudents.flattenedStudents = flattenedStudents;
      filteredStudents.students = students;
    } else {
      // filter flattenedStudents
      _.each(flattenedStudents, (flattenedStudent, idx) => {
        let doesPassFilters = _.every(filterFunctions, (func: Function) => func(flattenedStudent));
        if (doesPassFilters) {
          // push flattened
          filteredStudents.flattenedStudents.push(flattenedStudent);
          // push unflattened
          let unflattenedStudent = students[idx];
          filteredStudents.students.push(unflattenedStudent);
        }
      });
    }
    return filteredStudents;
  }

  sortFlattenedStudents(students, sort: IColumnSort[]) {
    if (!_.isUndefined(sort)) {
      const colIds = [];
      const sorts = [];

      _.each(sort, sortCol => {
        colIds.push(sortCol.colId);
        sorts.push(sortCol.sort);
      });
      students = _.orderBy(students, colIds, sorts);
    }
    return students;
  }

  getColumnDefs(columnKeys: string[], userMinis?: IUserMini[]) {
    let virtualColumnDef = {
      gridRowSelector: {
        colId: 'gridRowSelector',
        headerName: null,
        checkboxSelection: true,
        field: null,
        virtualField: 'gridRowSelector',
        pinned: 'left',
        width: 50,
        cellClass: 'center',
        suppressMenu: true,
      },
    };

    let colUnion = _.union(columnKeys, ['studentId', 'studentName']);

    let ret = _.map(columnKeys || colUnion, (col: string) => {
      // virtualCols are already projected to the format needed by AgGrid, so just return it.
      // The only virtualCol currently is the first column with the checkboxes (AKA gridRowSelector)
      let virtualCol = virtualColumnDef[col];
      if (virtualCol) {
        return virtualCol;
      }

      let sorterCol: ISorterColumn = this.SorterColumn.getByColumnKey(col);

      // The rest of this function converts a SorterCol into a Column Def for AgGrid
      // See properties here: https://www.ag-grid.com/javascript-grid-column-properties/#gsc.tab=0
      let filter;
      let cellEditor;
      let cellEditorParams;
      let cellRenderer;
      let cellRendererParams;
      let filterParams;
      let keyCreator;

      // dimensions
      let width = sorterCol.width || 90;

      // filters, cellEditors and cellRenderers
      switch (sorterCol.dataType) {
        case SorterColumnDataType.STRING:
          if (_.includes(sorterCol.path, 'notes.')) {
            cellEditor = 'largeText';
          } else {
            cellEditor = 'popupText';
          }
          filter = 'agSetColumnFilter';
          filterParams = {
            // include a formatter to handle case sensitivity, accents etc.
            textFormatter: this._caseInSensitiveFilter,
          };
          break;

        case SorterColumnDataType.BOOLEAN_YES_NO:
          cellEditor = 'agRichSelectCellEditor';
          let isBooleanEligibleVariant;
          if (sorterCol.dataTypeOptions && sorterCol.dataTypeOptions.isBooleanEligibleVariant) {
            isBooleanEligibleVariant = sorterCol.dataTypeOptions.isBooleanEligibleVariant;
          }
          cellEditorParams = {
            values: [true, false, '-'],
            cellRenderer: undefined,
          };
          if (isBooleanEligibleVariant) {
            cellEditorParams.cellRenderer = CellRenderer.renderBooleanAsEligible;
            cellRenderer = CellRenderer.renderBooleanAsEligible;
          } else {
            cellEditorParams.cellRenderer = CellRenderer.renderBooleanAsYesNo;
            cellRenderer = CellRenderer.renderBooleanAsYesNo;
          }
          filter = this.BooleanYesNoFilter;
          break;

        case SorterColumnDataType.ARRAY:
          cellRenderer = CellRenderer.renderArrayAsCSV;
          filter = 'agSetColumnFilter';
          filterParams = {
            // include a formatter to handle case sensitivity, accents etc.
            textFormatter: this._caseInSensitiveFilter,
          };
          break;

        case SorterColumnDataType.ENUM:
          cellEditor = 'agRichSelectCellEditor';
          const values = angular.copy(sorterCol.dataTypeOptions.values);
          if (sorterCol.dataTypeOptions.canBeNull) {
            values.push('-');
          }
          cellEditorParams = { values };
          cellRenderer = CellRenderer.renderEnumWithNull;
          filter = 'agSetColumnFilter';
          filterParams = {
            newRowsAction: 'keep',
            textFormatter: this._caseInSensitiveFilter,
          };
          break;

        case SorterColumnDataType.NUMERIC:
          cellEditor = 'popupText';
          if (sorterCol.dataTypeOptions.numberType === SorterColumnNumberType.PERCENT) {
            cellRenderer = CellRenderer.renderAsPercent;
          } else if (sorterCol.dataTypeOptions.numberType === SorterColumnNumberType.WITHOUT_DECIMAL) {
            cellRenderer = CellRenderer.renderWithoutDecimal;
          } else if (sorterCol.dataTypeOptions.numberType === SorterColumnNumberType.WITH_ONE_DECIMAL) {
            cellRenderer = CellRenderer.renderWithOneDecimal;
          } else if (sorterCol.dataTypeOptions.numberType === SorterColumnNumberType.WITH_NAN) {
            cellRenderer = CellRenderer.renderWithNaN;
          }
          filter = this.NumberFilter;
          break;

        case SorterColumnDataType.USER_MINI:
          if (!userMinis) {
            // tslint:disable-next-line:max-line-length
            const err = `Cannot load column "${
              sorterCol.humanName
            }" without providing student-set.service with userMinis arg`;
            throw new Error(err);
          }
          // tslint:disable-next-line:array-type
          const dataValidationVals: Array<IUserMini> = _.sortBy([...userMinis], 'lastName');

          // Allows "(none)" to be selected
          dataValidationVals.unshift({
            userId: null,
            gafeEmail: null,
            doeEmail: null,
            firstName: null,
            lastName: null,
          });

          cellEditor = 'agRichSelectCellEditor';
          cellRenderer = CellRenderer.renderUserMini;

          cellEditorParams = {
            cellRenderer,
            values: dataValidationVals,
          };

          keyCreator = params => {
            return params.value && params.value.lastName ? params.value.lastName : '(None)';
          };

          filter = 'agSetColumnFilter';

          filterParams = {
            // values: dataValidationVals.map((userMini) => this.imUser.getFullNameWithEmailFromMini(userMini)),
            newRowsAction: 'keep',
            textFormatter: this._caseInSensitiveFilter,
          };

          break;
        default:
          cellEditor = 'popupText';
      }

      let _editable = () => {
        return sorterCol.editable === true;
      };

      /**
       * Wraps each custom cellClassRule and uses the object passed in by AgGrid to each classRule to only pass
       * in the property in the AgGrid object that we care about - "value" - to
       * each classRule. Hence, we are  * * simplifying the AgGrid API by only requiring
       * that the calculation/value is passed as a parameter to each * custom class
       * function from the sorter constant.
       *
       * @param {Object} of cellClassRules:
       *    {
       *      'class-name-1': function(calculation) {},
       *      'class-name-2': function(calculation) {}
       *    }
       *
       * @returns {Object} of cellClassRules:
       *    {
       *      'class-name-1': function(calculation) {},
       *      'class-name-2': function(calculation) {}
       *    }
       */
      const _wrapCellClassRules = cellClassRules => {
        const wrappedCellClassRules = {};

        _.each(cellClassRules, (value: Function, key) => {
          return (wrappedCellClassRules[key] = params => {
            return value(params.value);
          });
        });

        return wrappedCellClassRules;
      };

      // This functionality handles nulls and empty strings in the set filter. Nulls are rendered as "-" and empty
      // strings are rendered as "(Empty)". The default set filter behavior is to combine nulls and empty strings
      // into one value for filtering that is "null", but this causes all sorts of issues with the rest of the
      // app.
      let _keyCreator;
      if (sorterCol.dataType === SorterColumnDataType.ENUM) {
        _keyCreator = params => {
          if (_.isNull(params.value)) {
            return '-';
          } else if (params.value === '') {
            return '(Empty)';
          } else {
            return params.value;
          }
        };
      }

      if (sorterCol.dataType === SorterColumnDataType.ARRAY) {
        _keyCreator = params => {
          if (params.value && params.value.length) {
            return params.value;
          }
          return '(Blanks)';
        };
      }

      const _defaultComparator = (val1, val2) => {
        if (_.isNil(val1) && _.isNil(val2)) {
          return 0;
        }
        if (_.isNil(val1)) {
          return -1;
        }

        if (_.isNil(val2)) {
          return 1;
        }

        if (typeof val1 === 'string' && typeof val2 === 'string') {
          return val1.toLowerCase().localeCompare(val2.toLowerCase());
        }

        return val1 > val2 ? 1 : val1 < val2 ? -1 : 0;
      };

      // comparator for our custom data type object USER_MINI
      const _userMiniComparator = (val1, val2) => {
        val1 = val1 && val1.lastName;
        val2 = val2 && val2.lastName;

        if (_.isNil(val1) && _.isNil(val2)) {
          return 0;
        }
        if (_.isNil(val1)) {
          return -1;
        }

        if (_.isNil(val2)) {
          return 1;
        }

        return val1.toLowerCase().localeCompare(val2.toLowerCase());
      };

      const _arrayComparator = (arr1, arr2) => {
        if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
          return;
        }

        if (arr1.length === 0 && arr2.length === 0) {
          return 0;
        }

        if (arr1.length === 0) {
          return -1;
        }

        if (arr2.length === 0) {
          return 1;
        }

        const val1 = arr1.reduce((accum, value) => accum + value).toLowerCase();
        const val2 = arr2.reduce((accum, value) => accum + value).toLowerCase();

        return val1.localeCompare(val2);
      };

      // this comparator is used in instances where a numerical data is represented as string
      const _stringNumericComparator = (val1, val2) => {
        if (_.isNil(val1) && _.isNil(val2)) {
          return 0;
        }

        if (_.isNil(val1)) {
          return -1;
        }

        if (_.isNil(val2)) {
          return 1;
        }

        if (typeof val1 === 'string' && typeof val2 === 'string') {
          return val1.localeCompare(val2, undefined, { numeric: true });
        }

        // shouldn't come here ideally, but wouldn't hurt to have a failsafe.
        return val1 > val2 ? 1 : val1 < val2 ? -1 : 0;
      };

      let _comparator = _defaultComparator;
      if (sorterCol.dataType === SorterColumnDataType.NUMERIC) {
        _comparator = _stringNumericComparator;
      } else if (sorterCol.dataType === SorterColumnDataType.ARRAY) {
        _comparator = _arrayComparator;
      } else if (sorterCol.dataType === SorterColumnDataType.USER_MINI) {
        _comparator = _userMiniComparator;
      }

      // TODO: Make an interface for this (JC)
      return {
        headerName: sorterCol.humanName,
        headerClass: _editable() ? 'isEditable' : null,
        headerTooltip: sorterCol.humanName,
        menuTabs: ['generalMenuTab', 'filterMenuTab'],
        field: col,
        filter,
        filterParams,
        pinned: sorterCol.pinned,
        minWidth: 90,
        width,
        sortable: true,
        // volatile: true,
        editable: _editable(),
        cellEditor,
        cellEditorParams,
        cellRenderer,
        cellRendererParams,
        tooltipField: sorterCol.tooltipCol,
        cellClassRules: angular.extend(
          {
            isEditable() {
              return sorterCol.editable === true;
            },
            center() {
              return sorterCol.dataType === SorterColumnDataType.NUMERIC;
            },
          },
          _wrapCellClassRules(sorterCol.formatClass),
        ),
        keyCreator: keyCreator || _keyCreator,
        comparator: _comparator,
      };
    });

    return ret;
  }

  // PRIVATE METHODS:
  _flattenStudent(student, school, columnKeys) {
    const flattenedStudent = {
      _id: null,
    };

    // set columnKeys on student:
    _.each(columnKeys, col => {
      this._setFlattenedStudentCol(flattenedStudent, col, student, student, school);
    });

    flattenedStudent._id = student._id;

    return flattenedStudent;
  }

  getProjectionForColumnKeys(columnKeys) {
    const projection = {};
    _.each(columnKeys, col => {
      const sorterCol = this.SorterColumn.getByColumnKey(col);

      if (sorterCol) {
        const paths = sorterCol.paths || [sorterCol.path];
        _.each(paths, path => (projection[path] = true));
      }
    });

    return this.UtilitiesService.reduceProjection(projection);
  }

  _getProjectionForPaths(paths) {
    const projection = {};
    _.each(paths, path => {
      projection[path] = true;
    });

    return this.UtilitiesService.reduceProjection(projection);
  }

  _setFlattenedStudentCol(flattenedStudent, col, student, studentPOJO, school) {
    let val;
    const sorterColumn = this.SorterColumn.getByColumnKey(col);
    if (sorterColumn.calculation) {
      // It seems like if an error is thrown in one of the sorterColumn.calculation functions,
      // the stack trace is lost. Catch and log to rollbar manually
      try {
        val = sorterColumn.calculation(student, school);
      } catch (err) {
        val = 'Bad calculation';
        const debug = {
          message: err.message,
          columnName: col,
          studentId: student._id,
          schoolId: school._id,
        };
        this.Rollbar.error(`Bad calculation for SorterColumn`, debug);
      }
    } else {
      const path = this.SorterColumn.getByColumnKey(col).path;
      val = this.UtilitiesService.getFieldByPath(studentPOJO, path);
    }
    flattenedStudent[col] = val;
  }

  _getJoinsForColumnKeys(columnKeys) {
    const allJoins = [];
    _.forEach(columnKeys, key => {
      const sorterCol = this.SorterColumn.getByColumnKey(key);

      if (!sorterCol) {
        this.Rollbar.warning(`Could not get joins for SorterColumn[${key}], skipping`);
        return;
      }

      if (sorterCol.joins) {
        // joins will be an array - - doesn't exist yet'
        _.forEach(sorterCol.joins, j => {
          allJoins.push(j);
        });
      }
    });
    return _.uniq(allJoins);
  }

  // when comparing to the text entered in the filter box, the text in the filter box is
  // always converted to lower case.
  // https://www.ag-grid.com/javascript-grid-filter-text/index.php#textFormatter&gsc.tab=0
  _caseInSensitiveFilter(filterStr) {
    if (filterStr == null) return null;
    return filterStr.toLowerCase();
  }
}

angular.module('nv.shared.services.sorter').service('StudentSet', StudentSetService);
